import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import {
  useAccessRules,
  useClubMutation,
  useDoors,
  useGroupManagers,
  useGroupMembers,
  useGroups,
  useLocations,
  usePersonAppStatus,
  usePersons,
} from '../../data/hooks';
import { Button, EmptyState, ErrorState, Loading, PageHeader, Panel, useToast } from '../../components/ui';
import { MembershipDialog } from '../../components/MembershipDialog';
import { AppStatusBadge, BulkInviteDialog } from '../../components/AppInvite';
import { appStatus, needsInvite } from '../../lib/appStatus';
import { GroupFormDialog } from './GroupsPage';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { fmtDate } from '../../lib/format';
import { hasDay, toggleDay, WEEKDAYS } from '../../lib/weekdays';
import { matrixToRules, rulesToMatrix, type MatrixRow } from '../../lib/matrix';
import type { Door } from '../../lib/types';

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const { orgId, role, managedGroupIds, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const groups = useGroups(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);
  const persons = usePersons(orgId);
  const doors = useDoors(orgId);
  const locations = useLocations(orgId);
  const managers = useGroupManagers(orgId);
  const [adding, setAdding] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [bulkInvite, setBulkInvite] = useState(false);
  const appRows = usePersonAppStatus(orgId);
  const remove = useClubMutation(orgId, (personId: string) => repo.removeGroupMember(groupId, personId));

  const qs = [groups, members, rules, persons, doors, locations, managers];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const group = groups.data!.find((g) => g.id === groupId);
  if (!group || !inScope(scope, 'groupIds', group.id)) {
    return <EmptyState title="Groep niet gevonden" action={<Link to={`/c/${orgId}/groepen`}>Terug naar groepen</Link>} />;
  }
  const personById = new Map(persons.data!.map((p) => [p.id, p]));
  const groupMembers = members
    .data!.filter((m) => m.group_id === groupId)
    .sort((a, b) => (personById.get(a.person_id)?.full_name ?? '').localeCompare(personById.get(b.person_id)?.full_name ?? '', 'nl'));
  const canEditMembers = can('group_member.edit', role) && !readOnly;
  const groupRules = rules.data!.filter((r) => r.group_id === groupId);
  const groupManagers = managers.data!.filter((m) => m.group_id === groupId);
  const appRowById = new Map((appRows.data ?? []).map((r) => [r.person_id, r]));
  const memberPersons = groupMembers.map((m) => personById.get(m.person_id)).filter((p): p is NonNullable<typeof p> => !!p);
  const inviteCandidates = memberPersons.filter((p) => needsInvite(appStatus(p, appRowById.get(p.id))));
  const canInvite = can('person.invite', role) && !readOnly;

  return (
    <div className="stack-lg">
      <PageHeader
        title={group.name}
        intro={[group.season_label ? `Seizoen ${group.season_label}` : null, group.valid_until ? `loopt tot ${fmtDate(group.valid_until)}` : 'geen einddatum'].filter(Boolean).join(', ')}
        actions={
          can('group.duplicate', role) &&
          !readOnly && (
            <Button onClick={() => setDuplicating(true)}>Dupliceren naar volgend seizoen</Button>
          )
        }
      />

      <AccessMatrix
        key={groupRules.map((r) => r.id).join()}
        groupId={groupId}
        doors={doors.data!}
        locations={locations.data!}
        rules={groupRules}
        editable={can('group.edit_rules', role) && !readOnly}
      />

      <Panel
        title={`Leden (${groupMembers.length})`}
        actions={
          <span className="row">
            {canInvite && groupMembers.length > 0 && (
              <Button size="sm" onClick={() => setBulkInvite(true)} disabled={!appRows.data}>
                Leden zonder app uitnodigen
              </Button>
            )}
            {canEditMembers && (
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                Lid toevoegen
              </Button>
            )}
          </span>
        }
      >
        {groupMembers.length === 0 ? (
          <EmptyState title="Nog geen leden">Voeg mensen toe of importeer ze vanuit Sportlink.</EmptyState>
        ) : (
          <ul className="tile-list">
            {groupMembers.map((m) => {
              const p = personById.get(m.person_id);
              return (
                <li key={m.person_id}>
                  <span>
                    <Link className="primary" to={`/c/${orgId}/personen/${m.person_id}`}>
                      {p?.full_name ?? 'Onbekend'}
                    </Link>
                    <span className="cell-sub">{m.valid_until ? `Tot en met ${fmtDate(m.valid_until)}` : 'Geen einddatum'}</span>
                  </span>
                  <span className="row">
                    {p && <AppStatusBadge status={appStatus(p, appRowById.get(p.id))} />}
                    {canEditMembers && (
                      <Button
                        size="sm"
                        disabled={remove.isPending}
                        onClick={async () => {
                          await remove.mutateAsync(m.person_id);
                          toast(`${p?.full_name} uit de groep gehaald. Intrekking wacht op sync.`);
                        }}
                      >
                        Uit groep halen
                      </Button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="Groepsbeheerders" description="Mogen leden toevoegen en verwijderen en codes tot 7 dagen maken voor de deuren van deze groep.">
        {groupManagers.length === 0 ? (
          <p className="tile-empty">Geen groepsbeheerders.</p>
        ) : (
          <ul className="tile-list">
            {groupManagers.map((m) => (
              <li key={m.profile_id}>
                <span className="primary">{m.profile.full_name}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {bulkInvite && (
        <BulkInviteDialog
          candidates={inviteCandidates}
          context={`Leden van ${group.name} die nog geen app hebben en niet al zijn uitgenodigd.`}
          onClose={() => setBulkInvite(false)}
        />
      )}

      {adding && (
        <MembershipDialog
          open
          onOpenChange={setAdding}
          groups={groups.data!}
          persons={persons.data!.filter((p) => !groupMembers.some((m) => m.person_id === p.id))}
          fixedGroup={group}
        />
      )}
      {duplicating && (
        <GroupFormDialog
          onClose={() => setDuplicating(false)}
          source={group}
          sourceMembers={groupMembers.map((m) => ({ id: m.person_id, name: personById.get(m.person_id)?.full_name ?? 'Onbekend' }))}
        />
      )}
    </div>
  );
}

function AccessMatrix({
  groupId,
  doors,
  locations,
  rules,
  editable,
}: {
  groupId: string;
  doors: Door[];
  locations: { id: string; name: string }[];
  rules: Parameters<typeof rulesToMatrix>[1];
  editable: boolean;
}) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const orderedDoors = useMemo(
    () => [...doors].sort((a, b) => a.location_id.localeCompare(b.location_id) || a.name.localeCompare(b.name, 'nl')),
    [doors],
  );
  const initial = useMemo(() => rulesToMatrix(orderedDoors.map((d) => d.id), rules), [orderedDoors, rules]);
  const [rows, setRows] = useState<MatrixRow[]>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setRows(initial), [initial]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);
  const save = useClubMutation(orgId, (r: Parameters<typeof repo.saveAccessRules>[1]) => repo.saveAccessRules(groupId, r));
  const locName = new Map(locations.map((l) => [l.id, l.name]));
  const visibleRows = editable ? rows : rows.filter((r) => r.weekdays > 0);

  const update = (doorId: string, patch: Partial<MatrixRow>) =>
    setRows((rs) => rs.map((r) => (r.door_id === doorId ? { ...r, ...patch } : r)));

  const onSave = async () => {
    const res = matrixToRules(rows);
    if (!res.ok) {
      setErrors(res.errors);
      return;
    }
    setErrors({});
    await save.mutateAsync(res.rules);
    toast('Toegangsregels opgeslagen. De deuren tonen "wacht op sync" tot een app ze bijwerkt.');
  };

  return (
    <Panel
      title="Toegang per deur en dag"
      description={editable ? 'Klik op een dag om toegang aan of uit te zetten. Eén tijdvenster per deur.' : 'Deuren en tijden van deze groep.'}
      actions={
        editable && (
          <span className="row">
            {dirty && (
              <Button size="sm" onClick={() => { setRows(initial); setErrors({}); }}>
                Herstel
              </Button>
            )}
            <Button variant="primary" size="sm" disabled={!dirty || save.isPending} onClick={() => void onSave()}>
              Regels opslaan
            </Button>
          </span>
        )
      }
    >
      {visibleRows.length === 0 ? (
        <p className="tile-empty">Deze groep heeft nog geen toegang tot een deur.</p>
      ) : (
        <div className="table-wrap">
          <table className="matrix">
            <caption className="sr-only">Toegangsmatrix: deuren tegen weekdagen</caption>
            <thead>
              <tr>
                <th scope="col" className="door-col">
                  Deur
                </th>
                {WEEKDAYS.map((d) => (
                  <th scope="col" key={d.bit} abbr={d.long}>
                    {d.short}
                  </th>
                ))}
                <th scope="col" style={{ textAlign: 'right', paddingRight: 16 }}>
                  Tijdvenster
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const door = orderedDoors.find((d) => d.id === row.door_id)!;
                return (
                  <tr key={row.door_id}>
                    <th scope="row" className="door-col" style={{ fontWeight: 600 }}>
                      {door.name}
                      <span className="cell-sub" style={{ fontWeight: 400 }}>
                        {locName.get(door.location_id)}
                        {!door.lock_id && ', nog geen slot'}
                      </span>
                      {errors[row.door_id] && (
                        <span className="error small" role="alert" style={{ display: 'block', color: 'var(--card-red)', fontWeight: 500 }}>
                          {errors[row.door_id]}
                        </span>
                      )}
                    </th>
                    {WEEKDAYS.map((d) => (
                      <td key={d.bit}>
                        <button
                          type="button"
                          className="day-cell"
                          aria-pressed={hasDay(row.weekdays, d.bit)}
                          aria-label={`${door.name}, ${d.long}`}
                          disabled={!editable}
                          onClick={() => update(row.door_id, { weekdays: toggleDay(row.weekdays, d.bit) })}
                        >
                          {hasDay(row.weekdays, d.bit) ? d.short : ''}
                        </button>
                      </td>
                    ))}
                    <td className="time-col">
                      {row.weekdays === 0 ? (
                        <span className="muted small">geen toegang</span>
                      ) : editable ? (
                        <div className="time-inputs">
                          <label className="row small" style={{ gap: 4 }}>
                            <input type="checkbox" checked={row.all_day} onChange={(e) => update(row.door_id, { all_day: e.target.checked })} />
                            hele dag
                          </label>
                          {!row.all_day && (
                            <>
                              <input
                                className="input"
                                type="time"
                                aria-label={`${door.name}, begintijd`}
                                value={row.start}
                                onChange={(e) => update(row.door_id, { start: e.target.value })}
                              />
                              <input
                                className="input"
                                type="time"
                                aria-label={`${door.name}, eindtijd`}
                                value={row.end === '24:00' ? '00:00' : row.end}
                                onChange={(e) => update(row.door_id, { end: e.target.value })}
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <span>{row.all_day ? 'hele dag' : `${row.start}–${row.end}`}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="matrix-legend" style={{ padding: '12px 20px' }}>
        <span>Tijden in Nederlandse tijd; het slot handhaaft ze zelf, ook zonder internet.</span>
      </div>
    </Panel>
  );
}
